import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Chip, Container, MenuItem, Paper, Select, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { getAuthUserId } from '../utils/authUser';
import { buildEventsApiUrl } from '../config/api';
import { isSiteAdminUser } from '../config/admin';
import { buildAuthHeaders } from '../utils/authFetch';

const formatTimestamp = (value?: string | null) => {
  if (!value) return 'Unknown time';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown time';
  return date.toLocaleString();
};

const SiteAdminOperationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const userId = getAuthUserId(user as any);
  const isAllowed = isSiteAdminUser(user as any);
  const [filter, setFilter] = useState<'all' | 'sessions' | 'groups' | 'issues' | 'reviews'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'staged' | 'completed' | 'canceled' | 'pending'>('all');
  const [search, setSearch] = useState('');
  const [operations, setOperations] = useState<{ sessions: any[]; groups: any[]; issues: any[]; reviews: any[] }>({ sessions: [], groups: [], issues: [], reviews: [] });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetch(buildEventsApiUrl('/api/site-admin/operations'), {
          headers: buildAuthHeaders(user),
        });
        const data = await response.json();
        if (!response.ok || cancelled) return;
        setOperations(data.operations || { sessions: [], groups: [], issues: [], reviews: [] });
      } catch {
        // ignore for now
      }
    };

    load();
    const intervalId = window.setInterval(load, 10000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [user]);

  const sections = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const matchesSearch = (item: any) => {
      if (!normalizedSearch) return true;
      return JSON.stringify(item).toLowerCase().includes(normalizedSearch);
    };
    const matchesStatus = (item: any) => {
      if (statusFilter === 'all') return true;
      return String(item?.status || '').toLowerCase() === statusFilter;
    };
    const filterItems = (items: any[], kind: string) => items.filter((item) => {
      if (!matchesSearch(item)) return false;
      if (kind === 'sessions' || kind === 'groups') {
        return matchesStatus(item);
      }
      return true;
    });

    const all = [
      { key: 'sessions', title: 'Trade Sessions', items: filterItems(operations.sessions, 'sessions') },
      { key: 'groups', title: 'Trade Groups', items: filterItems(operations.groups, 'groups') },
      { key: 'issues', title: 'Issues / Behavior Records', items: filterItems(operations.issues, 'issues') },
      { key: 'reviews', title: 'Reviews', items: filterItems(operations.reviews, 'reviews') },
    ];
    return filter === 'all' ? all : all.filter((section) => section.key === filter);
  }, [filter, operations, search, statusFilter]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Stack spacing={2.5}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Site Admin Event-Day Operations</Typography>
          <Typography variant="body2" color="text.secondary">
            Fuller operational view with auto-refresh for sessions, groups, issues, and reviews.
          </Typography>

          {!isAllowed && (
            <Alert severity="warning">
              You are signed in as <strong>{userId || 'unknown'}</strong>. This area is intended for site admins.
            </Alert>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Typography variant="body2" color="text.secondary">Filters</Typography>
            <Select size="small" value={filter} onChange={(e) => setFilter(e.target.value as any)} sx={{ minWidth: 180 }}>
              <MenuItem value="all">All sections</MenuItem>
              <MenuItem value="sessions">Sessions</MenuItem>
              <MenuItem value="groups">Groups</MenuItem>
              <MenuItem value="issues">Issues</MenuItem>
              <MenuItem value="reviews">Reviews</MenuItem>
            </Select>
            <Select size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)} sx={{ minWidth: 180 }}>
              <MenuItem value="all">All statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="staged">Staged</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="canceled">Canceled</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
            <TextField
              size="small"
              placeholder="Search event id, group, status..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 260 }}
            />
          </Stack>

          {sections.map((section) => (
            <Paper key={section.key} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack spacing={1.2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Typography variant="h6">{section.title}</Typography>
                  <Chip label={`${section.items.length} shown`} size="small" color="primary" variant="outlined" />
                </Stack>
                {section.items.length > 0 ? section.items.map((item: any) => (
                  <Paper key={item.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Stack spacing={0.5}>
                      <Button
                        variant="text"
                        onClick={() => navigate(`/events/${item.eventId}`)}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none', px: 0, py: 0 }}
                      >
                        {section.key === 'sessions' && `${item.locationName || 'Unassigned location'} · ${item.status} · event ${item.eventId}`}
                        {section.key === 'groups' && `Group ${item.groupKey} · ${item.participantCount || item.participantUserIds?.length || 0} participants · ${item.status} · event ${item.eventId}`}
                        {section.key === 'issues' && `${item.outcomeType || 'behavior-record'} · event ${item.eventId}`}
                        {section.key === 'reviews' && `${item.reviewStatus || 'review'} · ${item.rating || 0}★ · event ${item.eventId}`}
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        {section.key === 'sessions' && `Initiated ${formatTimestamp(item.initiatedAt)}`}
                        {section.key === 'groups' && `Updated ${formatTimestamp(item.updatedAt)}`}
                        {section.key === 'issues' && `Created ${formatTimestamp(item.createdAt)}`}
                        {section.key === 'reviews' && `Created ${formatTimestamp(item.createdAt)}`}
                      </Typography>
                    </Stack>
                  </Paper>
                )) : (
                  <Typography variant="body2" color="text.secondary">No items in this section right now.</Typography>
                )}
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Paper>
    </Container>
  );
};

export default SiteAdminOperationsPage;
